import React, { useState, useEffect, useMemo } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useLang } from '../contexts/LangContext';
import { useAuth } from '../contexts/AuthContext';
import toast from 'react-hot-toast';
import { buildingsApi, Building } from '../api/buildingsApi';
import { applyDefaultBuildingSelection, needsBuildingSelection, shouldShowBuildingSelector, getBuildingSelectProps, selectBuilding } from '../utils/buildingSelection';
import { apartmentsApi, ApartmentGroup, Apartment } from '../api/apartmentsApi';
import { pdfExportsApi, PdfExportRecord } from '../api/pdfExportsApi';
import ApartmentGroupMultiSelect from '../components/apartments/ApartmentGroupMultiSelect';
import ApartmentMultiSelect from '../components/apartments/ApartmentMultiSelect';
import FeeCategoryMultiSelect from '../components/fee-categories/FeeCategoryMultiSelect';
import { feeCategoriesApi, FeeCategory } from '../api/feeCategoriesApi';
import { getApiErrorMessage } from '../utils/apiErrors';
import {
  billingPeriodSlashToDash,
  generateFeeNoticeExportPeriodOptions,
  generateBillingPeriodOptions,
} from '../utils/billingPeriods';
import { getCurrentBillingPeriodValue } from '../utils/billingPeriodPicker';
import FeeNotificationDeliveryModal from '../components/modals/FeeNotificationDeliveryModal';
import type { PdfExportNoticeItem } from '../api/pdfExportsApi';

interface BillingPeriod {
  value: string;
  label: string;
}

export const ExportPdfServiceFees: React.FC = () => {
  const { t } = useLang();
  const { user } = useAuth();
  const navigate = useNavigate();

  const [selectedBuilding, setSelectedBuilding] = useState<string>('');
  const [selectedPeriod, setSelectedPeriod] = useState<string>('');
  const [noticeIssueLabel, setNoticeIssueLabel] = useState<string>('');
  const [onlyOutstanding, setOnlyOutstanding] = useState<boolean>(true);
  const [includeAllUnpaidOnNotice, setIncludeAllUnpaidOnNotice] = useState(false);
  const [onlyWithoutApp, setOnlyWithoutApp] = useState<boolean>(false);
  const [selectedGroupIds, setSelectedGroupIds] = useState<string[]>([]);
  const [selectedApartmentIds, setSelectedApartmentIds] = useState<string[]>([]);
  const [feeCategories, setFeeCategories] = useState<FeeCategory[]>([]);
  const [selectedFeeCategoryIds, setSelectedFeeCategoryIds] = useState<string[]>([]);
  const [apartments, setApartments] = useState<Apartment[]>([]);
  const [email, setEmail] = useState<string>('');
  const [isExporting, setIsExporting] = useState<boolean>(false);
  const [exportingType, setExportingType] = useState<'PDF' | 'PRINT_HTML' | null>(null);

  const [buildings, setBuildings] = useState<Building[]>([]);
  const [apartmentGroups, setApartmentGroups] = useState<ApartmentGroup[]>([]);
  const [billingPeriods, setBillingPeriods] = useState<BillingPeriod[]>([]);
  const [exportHistory, setExportHistory] = useState<PdfExportRecord[]>([]);
  const [historyPage, setHistoryPage] = useState(1);
  const [historyTotalPages, setHistoryTotalPages] = useState(1);
  const [historyTotal, setHistoryTotal] = useState(0);
  const [isLoadingBuildings, setIsLoadingBuildings] = useState<boolean>(true);
  const [isLoadingHistory, setIsLoadingHistory] = useState<boolean>(true);
  const [downloadingExportId, setDownloadingExportId] = useState<string | null>(null);
  const [sendingExportId, setSendingExportId] = useState<string | null>(null);
  const [sendTarget, setSendTarget] = useState<{
    exportId: string;
    item: PdfExportNoticeItem;
  } | null>(null);
  const [showSendModal, setShowSendModal] = useState(false);
  const [sentSingleExportIds, setSentSingleExportIds] = useState<Set<string>>(() => new Set());

  const historyPageSize = 20;
  const [historyPeriodFilter, setHistoryPeriodFilter] = useState('');
  const [historyUserQuery, setHistoryUserQuery] = useState('');
  const [historyUserFilter, setHistoryUserFilter] = useState('');
  const [historyApartmentQuery, setHistoryApartmentQuery] = useState('');
  const [historyApartmentFilter, setHistoryApartmentFilter] = useState('');

  const historyBillingPeriods = useMemo(
    (): BillingPeriod[] =>
      generateBillingPeriodOptions(36, 6).map((period) => ({
        value: billingPeriodSlashToDash(period.value),
        label: period.label,
      })),
    [],
  );

  const buildingGroups = useMemo(
    () =>
      apartmentGroups.filter(
        (group) => group.buildingId === selectedBuilding && group.status === 'ACTIVE',
      ),
    [apartmentGroups, selectedBuilding],
  );

  const loadBuildings = async () => {
    try {
      setIsLoadingBuildings(true);
      const data = await buildingsApi.getBuildings();
      setBuildings(data);
      setSelectedBuilding((prev) => applyDefaultBuildingSelection(data, user, prev));
    } catch (error) {
      console.error('Error loading buildings:', error);
      toast.error('Có lỗi xảy ra khi tải danh sách chung cư');
    } finally {
      setIsLoadingBuildings(false);
    }
  };

  const loadApartmentGroups = async () => {
    try {
      const data = await apartmentsApi.getApartmentGroups();
      setApartmentGroups(data);
    } catch (error) {
      console.error('Error loading apartment groups:', error);
    }
  };

  const loadExportHistory = async (page = historyPage) => {
    if (!selectedBuilding) {
      setExportHistory([]);
      setHistoryTotal(0);
      setHistoryTotalPages(1);
      setIsLoadingHistory(false);
      return;
    }
    try {
      setIsLoadingHistory(true);
      const data = await pdfExportsApi.getHistory({
        page,
        limit: historyPageSize,
        buildingId: selectedBuilding,
        billingPeriod: historyPeriodFilter || undefined,
        requestedBy: historyUserFilter || undefined,
        apartment: historyApartmentFilter || undefined,
      });
      setExportHistory(data.items);
      setHistoryPage(data.pagination.page);
      setHistoryTotalPages(data.pagination.totalPages);
      setHistoryTotal(data.pagination.total);
    } catch (error) {
      console.error('Error loading export history:', error);
    } finally {
      setIsLoadingHistory(false);
    }
  };

  const loadFeeCategories = async (buildingId: string) => {
    try {
      const data = await feeCategoriesApi.getAll(buildingId);
      const active = data.filter((cat) => cat.isActive !== false);
      setFeeCategories(active);
      setSelectedFeeCategoryIds(active.map((cat) => cat.id));
    } catch (error) {
      console.error('Error loading fee categories:', error);
      setFeeCategories([]);
      setSelectedFeeCategoryIds([]);
    }
  };

  const loadApartments = async (buildingId: string) => {
    try {
      const apartmentList = await apartmentsApi.getApartments({ buildingId });
      setApartments(apartmentList.filter((apt) => apt.status === 'ACTIVE'));
    } catch (error) {
      console.error('Error loading apartments:', error);
      setApartments([]);
    }
  };

  const handleDownloadZip = async (record: PdfExportRecord) => {
    if (!record.downloadUrl) {
      toast.error('Không có file tải xuống');
      return;
    }
    try {
      setDownloadingExportId(record.id);
      const zipName = `thong-bao-phi-${record.billingPeriod}-${record.id.slice(0, 8)}.zip`;
      await pdfExportsApi.downloadZip(record.downloadUrl, zipName);
    } catch (error: unknown) {
      console.error('Download export zip failed:', error);
      toast.error(getApiErrorMessage(error, 'Không thể tải file ZIP'));
    } finally {
      setDownloadingExportId(null);
    }
  };

  const openSendForExport = async (record: PdfExportRecord) => {
    try {
      setSendingExportId(record.id);
      const data = await pdfExportsApi.getNotices(record.id, { limit: 500 });
      if (data.items.length === 0) {
        toast.error('Không có phiếu trong batch');
        return;
      }
      if (data.items.length === 1) {
        setSendTarget({ exportId: record.id, item: data.items[0] });
        setShowSendModal(true);
        return;
      }
      toast('Batch nhiều căn — chọn Gửi từng căn trong danh sách phiếu', { icon: 'ℹ️' });
      navigate(`/pdf-exports/${record.id}/notices`);
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Không thể mở gửi thông báo phí'));
    } finally {
      setSendingExportId(null);
    }
  };

  const handleConfirmSendNotice = async (deliveryMethods: string[]) => {
    if (!sendTarget) return;
    try {
      setSendingExportId(sendTarget.exportId);
      const result = await pdfExportsApi.sendNotice(sendTarget.exportId, sendTarget.item.id, {
        deliveryMethods: deliveryMethods as ('EMAIL' | 'APP')[],
      });
      if (result.success) {
        toast.success(`Đã gửi thông báo phí ${sendTarget.item.code}`);
        setSentSingleExportIds((prev) => new Set(prev).add(sendTarget.exportId));
        setExportHistory((prev) =>
          prev.map((row) =>
            row.id === sendTarget.exportId
              ? {
                  ...row,
                  feeNoticeSentAt: new Date().toISOString(),
                  feeNoticeSendSuccessCount:
                    (row.feeNoticeSendSuccessCount ?? 0) + 1,
                }
              : row,
          ),
        );
        setShowSendModal(false);
        setSendTarget(null);
      } else {
        toast.error(result.errors.join('; ') || 'Gửi thông báo phí thất bại');
      }
    } catch (error) {
      toast.error(getApiErrorMessage(error, 'Gửi thông báo phí thất bại'));
    } finally {
      setSendingExportId(null);
    }
  };

  const handleExport = async (exportType: 'PDF' | 'PRINT_HTML') => {
    if (!selectedBuilding || !selectedPeriod) {
      toast.error(t('pleaseSelectBuildingAndPeriod'));
      return;
    }

    if (
      feeCategories.length > 0 &&
      selectedFeeCategoryIds.length === 0
    ) {
      toast.error('Vui lòng chọn ít nhất một danh mục phí');
      return;
    }

    const allCategoriesSelected =
      feeCategories.length === 0 ||
      selectedFeeCategoryIds.length === feeCategories.length;

    try {
      setIsExporting(true);
      setExportingType(exportType);

      await pdfExportsApi.requestExport({
        buildingId: selectedBuilding,
        billingPeriod: selectedPeriod,
        ...(noticeIssueLabel.trim()
          ? { noticeIssueLabel: noticeIssueLabel.trim() }
          : {}),
        onlyOutstanding,
        includeAllUnpaidOnNotice,
        onlyWithoutApp,
        apartmentGroupIds:
          selectedApartmentIds.length > 0
            ? undefined
            : selectedGroupIds.length > 0
              ? selectedGroupIds
              : undefined,
        apartmentIds: selectedApartmentIds.length > 0 ? selectedApartmentIds : undefined,
        ...(!allCategoriesSelected && selectedFeeCategoryIds.length > 0
          ? { feeCategoryIds: selectedFeeCategoryIds }
          : {}),
        exportType,
        ...(email.trim() ? { email: email.trim() } : {}),
      });

      toast.success(
        exportType === 'PRINT_HTML'
          ? 'Đã tạo lô xuất file in. Vui lòng chờ xử lý...'
          : t('exportRequestSent'),
      );

      setSelectedGroupIds([]);
      setSelectedApartmentIds([]);
      setEmail('');
      setHistoryPage(1);
      loadExportHistory(1);
    } catch (error: unknown) {
      console.error('Error submitting export request:', error);
      toast.error(getApiErrorMessage(error, t('errorSubmittingExportRequest')));
    } finally {
      setIsExporting(false);
      setExportingType(null);
    }
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return date.toLocaleString('vi-VN', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
    });
  };

  const getStatusBadgeColor = (status: string) => {
    switch (status) {
      case 'processing':
        return 'bg-yellow-100 text-yellow-800';
      case 'completed':
        return 'bg-green-100 text-green-800';
      case 'failed':
        return 'bg-red-100 text-red-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };

  const normalizeExportStatus = (status: string) => status?.toLowerCase?.() ?? '';

  const getStatusText = (status: string) => {
    switch (normalizeExportStatus(status)) {
      case 'processing':
        return t('processingExport');
      case 'completed':
        return t('completed');
      case 'failed':
        return t('failed');
      default:
        return status;
    }
  };

  useEffect(() => {
    setBillingPeriods(generateFeeNoticeExportPeriodOptions());
    setSelectedPeriod(billingPeriodSlashToDash(getCurrentBillingPeriodValue()));
    loadBuildings();
    loadApartmentGroups();
  }, []);

  useEffect(() => {
    loadExportHistory(historyPage);
  }, [
    historyPage,
    historyPeriodFilter,
    historyUserFilter,
    historyApartmentFilter,
    selectedBuilding,
  ]);

  useEffect(() => {
    setSelectedGroupIds([]);
    setSelectedApartmentIds([]);
    if (selectedBuilding) {
      loadApartments(selectedBuilding);
      loadFeeCategories(selectedBuilding);
    } else {
      setApartments([]);
      setFeeCategories([]);
      setSelectedFeeCategoryIds([]);
    }
  }, [selectedBuilding]);

  return (
    <div className="max-w-7xl mx-auto">
      <div className="mb-8">
        <h1 className="text-3xl font-bold text-gray-900 mb-2">
          {t('exportPdfTitle')}
        </h1>
        <p className="text-gray-600">{t('exportPdfDescription')}</p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200">
            <h2 className="text-lg font-semibold text-gray-900">
              {t('exportPdfServiceFees')}
            </h2>
          </div>

          <div className="p-6 space-y-6">
            {shouldShowBuildingSelector(buildings) && (
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-2">
                  {t('selectBuildingForExport')}
                </label>
                <select
                  value={selectedBuilding}
                  onChange={(e) => {
                    selectBuilding(user?.id, e.target.value, setSelectedBuilding);
                    setHistoryPage(1);
                  }}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  disabled={isLoadingBuildings || !needsBuildingSelection(buildings)}
                >
                  <option value="">{t('selectBuildingPlaceholder')}</option>
                  {buildings.map((building) => (
                    <option key={building.id} value={building.id}>
                      {building.name} - {building.operator?.name || 'No operator'}
                    </option>
                  ))}
                </select>
              </div>
            )}

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('selectBillingPeriodToExport')}
              </label>
              <select
                value={selectedPeriod}
                onChange={(e) => setSelectedPeriod(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              >
                <option value="">{t('selectBillingPeriod')}</option>
                {billingPeriods.map((period) => (
                  <option key={period.value} value={period.value}>
                    {period.label}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Có thể chọn kỳ tương lai (vd. 11/2026 thu trước) sau khi đã Tạo phí kỳ đó.
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                Lần thông báo
                <span className="text-gray-400 font-normal ml-1">(tùy chọn)</span>
              </label>
              <input
                type="text"
                inputMode="numeric"
                pattern="[0-9]*"
                value={noticeIssueLabel}
                onChange={(e) => {
                  const digits = e.target.value.replace(/\D/g, '');
                  setNoticeIssueLabel(digits);
                }}
                placeholder="1, 2, 3…"
                className="w-full max-w-[8rem] px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
              <p className="mt-1 text-xs text-gray-500">
                Chỉ nhập số. Hệ thống tự thêm LẦN / 2ND NOTICE trên tiêu đề (vd. LẦN 2, 2ND NOTICE).
              </p>
            </div>

            {selectedBuilding && (
              <div className="space-y-4">
                <ApartmentGroupMultiSelect
                  groups={buildingGroups.map((group) => ({ id: group.id, name: group.name }))}
                  selectedGroupIds={selectedGroupIds}
                  onChange={(groupIds) => {
                    setSelectedGroupIds(groupIds);
                    setSelectedApartmentIds([]);
                  }}
                  helperText="Bỏ trống để xuất tất cả căn hộ trong chung cư."
                />
                <ApartmentMultiSelect
                  apartments={apartments}
                  selectedApartmentIds={selectedApartmentIds}
                  selectedGroupIds={selectedGroupIds}
                  onChange={setSelectedApartmentIds}
                />
                <FeeCategoryMultiSelect
                  categories={feeCategories.map((cat) => ({
                    id: cat.id,
                    name: cat.name,
                    feeCode: cat.feeCode,
                  }))}
                  selectedCategoryIds={selectedFeeCategoryIds}
                  onChange={setSelectedFeeCategoryIds}
                  disabled={!selectedBuilding || feeCategories.length === 0}
                />
              </div>
            )}

            <div className="space-y-3">
              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={onlyOutstanding}
                  onChange={(e) => setOnlyOutstanding(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  {t('onlyExportOutstandingFees')}
                </span>
              </label>

              <label className="flex items-start space-x-3">
                <input
                  type="checkbox"
                  checked={includeAllUnpaidOnNotice}
                  onChange={(e) => setIncludeAllUnpaidOnNotice(e.target.checked)}
                  className="w-4 h-4 mt-0.5 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Tất cả phí còn nợ (đến kỳ thông báo đã chọn)
                  <span className="block text-xs text-gray-500 mt-0.5 font-normal">
                    Liệt kê mọi hóa đơn chưa thanh toán có kỳ ≤ kỳ TB (vd. PQL 10/2026 và nước
                    10/2026 trên thông báo 10/2026), không chỉ dòng theo quy tắc TB tháng. Phù hợp
                    chuyển căn / thu gộp trước khi quét QR.
                  </span>
                </span>
              </label>

              <label className="flex items-center space-x-3">
                <input
                  type="checkbox"
                  checked={onlyWithoutApp}
                  onChange={(e) => setOnlyWithoutApp(e.target.checked)}
                  className="w-4 h-4 text-blue-600 border-gray-300 rounded focus:ring-blue-500"
                />
                <span className="text-sm text-gray-700">
                  Căn hộ chưa cài app
                </span>
              </label>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-700 mb-2">
                {t('emailToReceiveDownloadLink')}{' '}
                <span className="text-gray-400 font-normal">
                  ({t('optional') || 'tùy chọn'})
                </span>
              </label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('enterEmailToReceiveLink')}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
              />
            </div>

            <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
              <button
                onClick={() => handleExport('PRINT_HTML')}
                disabled={isExporting || !selectedBuilding || !selectedPeriod}
                className="w-full bg-green-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-green-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting && exportingType === 'PRINT_HTML'
                  ? 'Đang xuất...'
                  : 'XUẤT FILE IN'}
              </button>
              <button
                onClick={() => handleExport('PDF')}
                disabled={isExporting || !selectedBuilding || !selectedPeriod}
                className="w-full bg-blue-600 text-white py-3 px-4 rounded-lg font-medium hover:bg-blue-700 disabled:bg-gray-300 disabled:cursor-not-allowed transition-colors"
              >
                {isExporting && exportingType === 'PDF'
                  ? t('exporting')
                  : 'XUẤT FILE PDF'}
              </button>
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200">
          <div className="p-6 border-b border-gray-200 flex items-center justify-between gap-4">
            <h2 className="text-lg font-semibold text-gray-900">
              Lịch sử xuất thông báo phí
            </h2>
            <button
              type="button"
              onClick={() => loadExportHistory(historyPage)}
              disabled={isLoadingHistory}
              className="inline-flex items-center gap-2 px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
            >
              <svg
                className={`w-4 h-4 ${isLoadingHistory ? 'animate-spin' : ''}`}
                fill="none"
                stroke="currentColor"
                viewBox="0 0 24 24"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 4v5h.582m15.356 2A8.001 8.001 0 004.582 9m0 0H9m11 11v-5h-.581m0 0a8.003 8.003 0 01-15.357-2m15.357 2H15"
                />
              </svg>
              Làm mới
            </button>
          </div>

          <div className="px-6 py-4 border-b border-gray-100 bg-gray-50/80">
            <form
              className="flex flex-wrap items-end gap-3"
              onSubmit={(e) => {
                e.preventDefault();
                setHistoryPage(1);
                setHistoryUserFilter(historyUserQuery.trim());
                setHistoryApartmentFilter(historyApartmentQuery.trim());
              }}
            >
              <div className="min-w-[180px]">
                <label className="block text-xs font-medium text-gray-600 mb-1">Kỳ phí</label>
                <select
                  value={historyPeriodFilter}
                  onChange={(e) => {
                    setHistoryPeriodFilter(e.target.value);
                    setHistoryPage(1);
                  }}
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg bg-white"
                >
                  <option value="">Tất cả kỳ</option>
                  {historyBillingPeriods.map((period) => (
                    <option key={period.value} value={period.value}>
                      {period.label}
                    </option>
                  ))}
                </select>
              </div>
              <div className="min-w-[160px] flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Căn hộ</label>
                <input
                  type="text"
                  value={historyApartmentQuery}
                  onChange={(e) => setHistoryApartmentQuery(e.target.value)}
                  placeholder="Mã căn hộ, mã KH"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
              <div className="min-w-[160px] flex-1">
                <label className="block text-xs font-medium text-gray-600 mb-1">Người tạo</label>
                <input
                  type="text"
                  value={historyUserQuery}
                  onChange={(e) => setHistoryUserQuery(e.target.value)}
                  placeholder="Tên hoặc email nhân viên"
                  className="w-full px-3 py-2 text-sm border border-gray-300 rounded-lg"
                />
              </div>
              <button
                type="submit"
                className="px-4 py-2 text-sm font-medium text-white bg-blue-600 rounded-lg hover:bg-blue-700"
              >
                Tìm
              </button>
              {(historyPeriodFilter || historyUserFilter || historyApartmentFilter) && (
                <button
                  type="button"
                  className="px-4 py-2 text-sm border border-gray-300 rounded-lg hover:bg-white"
                  onClick={() => {
                    setHistoryPeriodFilter('');
                    setHistoryUserQuery('');
                    setHistoryUserFilter('');
                    setHistoryApartmentQuery('');
                    setHistoryApartmentFilter('');
                    setHistoryPage(1);
                  }}
                >
                  Xóa lọc
                </button>
              )}
            </form>
          </div>

          <div className="p-6 overflow-x-auto">
            {isLoadingHistory ? (
              <div className="text-center py-8">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto" />
                <p className="mt-2 text-gray-500">{t('loading')}</p>
              </div>
            ) : exportHistory.length === 0 ? (
              <div className="text-center py-8 text-gray-500">
                {t('noExportHistory')}
              </div>
            ) : (
              <table className="min-w-full text-sm">
                <thead>
                  <tr className="text-left text-xs font-semibold text-gray-500 uppercase border-b">
                    <th className="pb-3 pr-3">Kỳ phí</th>
                    <th className="pb-3 pr-3">Lần thông báo</th>
                    <th className="pb-3 pr-3">Ngày tạo</th>
                    <th className="pb-3 pr-3">Tên phiếu</th>
                    <th className="pb-3 pr-3">Số phiếu</th>
                    <th className="pb-3 pr-3">Người tạo</th>
                    <th className="pb-3 pr-3">Phạm vi xuất</th>
                    <th className="pb-3 pr-3">Danh mục phí</th>
                    <th className="pb-3">Thao tác</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {exportHistory.map((record) => (
                    <tr key={record.id} className="align-top">
                      <td className="py-3 pr-3 text-gray-900">
                        {record.billingPeriodDisplay}
                      </td>
                      <td className="py-3 pr-3 text-gray-700 whitespace-nowrap">
                        {record.noticeIssueDisplay?.trim() ||
                          (record.noticeIssueLabel?.trim()
                            ? `LẦN ${record.noticeIssueLabel.replace(/\D/g, '')}`
                            : '—')}
                      </td>
                      <td className="py-3 pr-3 text-gray-600 whitespace-nowrap">
                        {formatDateTime(record.createdAt)}
                      </td>
                      <td className="py-3 pr-3 text-gray-900 max-w-[180px]">
                        <div className="font-medium">{record.title}</div>
                        <span
                          className={`inline-flex mt-1 px-2 py-0.5 rounded-full text-xs ${getStatusBadgeColor(record.status)}`}
                        >
                          {getStatusText(record.status)}
                        </span>
                      </td>
                      <td className="py-3 pr-3 text-gray-900">
                        {record.noticeCount ?? '-'}
                      </td>
                      <td className="py-3 pr-3 text-gray-600">
                        {record.requestedByName || '-'}
                      </td>
                      <td className="py-3 pr-3 text-gray-600 max-w-[120px]">
                        {record.groupLabel || 'Tất cả'}
                        {record.onlyWithoutApp && (
                          <div className="text-xs text-amber-600 mt-1">
                            Chưa cài app
                          </div>
                        )}
                      </td>
                      <td className="py-3 pr-3 text-gray-600 max-w-[160px]">
                        {record.feeCategoryLabel?.trim() || 'Tất cả'}
                      </td>
                      <td className="py-3">
                        {normalizeExportStatus(record.status) === 'completed' &&
                        record.exportType === 'print_html' ? (
                          <div className="flex items-center gap-2">
                            <Link
                              to={`/pdf-exports/${record.id}/notices`}
                              className="p-2 rounded-full bg-gray-100 text-gray-700 hover:bg-gray-200"
                              title="Xem danh sách"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M2.458 12C3.732 7.943 7.523 5 12 5c4.478 0 8.268 2.943 9.542 7-1.274 4.057-5.064 7-9.542 7-4.477 0-8.268-2.943-9.542-7z" />
                              </svg>
                            </Link>
                            <button
                              type="button"
                              onClick={() =>
                                window.open(
                                  pdfExportsApi.getBatchPrintHtmlUrl(record.id, {
                                    onlyWithoutApp: record.onlyWithoutApp,
                                  }),
                                  '_blank',
                                )
                              }
                              className="p-2 rounded-full bg-green-600 text-white hover:bg-green-700"
                              title="In tất cả"
                            >
                              <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M17 17h2a2 2 0 002-2v-4a2 2 0 00-2-2H5a2 2 0 00-2 2v4a2 2 0 002 2h2m2 4h6a2 2 0 002-2v-4a2 2 0 00-2-2H9a2 2 0 00-2 2v4a2 2 0 002 2zm8-12V5a2 2 0 00-2-2H9a2 2 0 00-2 2v4h10z" />
                              </svg>
                            </button>
                            <button
                              type="button"
                              disabled={sendingExportId === record.id}
                              onClick={() => openSendForExport(record)}
                              className="px-2.5 py-1.5 rounded-lg bg-blue-600 text-white text-xs font-medium hover:bg-blue-700 disabled:opacity-50 whitespace-nowrap"
                              title="Gửi email / push (HTML đã lưu trong batch)"
                            >
                              {sendingExportId === record.id
                                ? '...'
                                : record.feeNoticeSentAt ||
                                    sentSingleExportIds.has(record.id)
                                  ? 'Gửi lại'
                                  : 'Gửi'}
                            </button>
                          </div>
                        ) : normalizeExportStatus(record.status) === 'completed' &&
                          record.downloadUrl ? (
                          <button
                            type="button"
                            disabled={downloadingExportId === record.id}
                            onClick={() => handleDownloadZip(record)}
                            className="inline-flex items-center px-3 py-1.5 text-xs font-medium rounded text-blue-700 bg-blue-100 hover:bg-blue-200 disabled:opacity-50"
                          >
                            {downloadingExportId === record.id ? 'Đang tải...' : 'Tải ZIP'}
                          </button>
                        ) : normalizeExportStatus(record.status) === 'processing' ? (
                          <span className="text-xs text-yellow-700">Đang xử lý...</span>
                        ) : normalizeExportStatus(record.status) === 'failed' ? (
                          <span
                            className="text-xs text-red-700 max-w-[140px] inline-block"
                            title={record.errorMessage || undefined}
                          >
                            {t('failed')}
                            {record.errorMessage ? `: ${record.errorMessage}` : ''}
                          </span>
                        ) : (
                          <span className="text-xs text-gray-500">—</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            )}
            {!isLoadingHistory && exportHistory.length > 0 && (
              <div className="flex flex-wrap items-center justify-between gap-3 mt-4 pt-4 border-t border-gray-100">
                <p className="text-sm text-gray-600">
                  Tổng {historyTotal} lô · Trang {historyPage}/{historyTotalPages}
                </p>
                <div className="flex items-center gap-2">
                  <button
                    type="button"
                    disabled={historyPage <= 1 || isLoadingHistory}
                    onClick={() => setHistoryPage((p) => Math.max(1, p - 1))}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Trước
                  </button>
                  <button
                    type="button"
                    disabled={historyPage >= historyTotalPages || isLoadingHistory}
                    onClick={() => setHistoryPage((p) => p + 1)}
                    className="px-3 py-1.5 text-sm border border-gray-300 rounded-lg hover:bg-gray-50 disabled:opacity-50"
                  >
                    Sau
                  </button>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <FeeNotificationDeliveryModal
        isOpen={showSendModal}
        mode="MONTHLY_FEE"
        onClose={() => {
          if (sendingExportId) return;
          setShowSendModal(false);
          setSendTarget(null);
        }}
        onConfirm={handleConfirmSendNotice}
        sending={Boolean(sendingExportId && sendTarget)}
      />
    </div>
  );
};

export default ExportPdfServiceFees;
